import type { SupportCaseDto } from '@/generated/api/pacApi.schemas.ts';
import { DateTime } from 'luxon';
import {
  ActionIcon,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { SupportCaseStatusBadge } from '@/features/support-cases/components/SupportCaseStatusBadge.tsx';
import { notifications } from '@mantine/notifications';
import { CopyIcon } from '@phosphor-icons/react';
import { useClipboard } from '@mantine/hooks';
import AnchorButton from '@/common/components/AnchorButton.tsx';
import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';
import HrefButton from '@/common/components/HrefButton.tsx';

export default function SupportCaseHeader({
  supportCase,
}: {
  supportCase: SupportCaseDto;
}) {
  const sidebarStack = useSidebarStack();
  const clipboard = useClipboard();

  return (
    <Paper withBorder radius="md" p="sm">
      <SimpleGrid cols={2} spacing="sm">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Case #
          </Text>
          <Group gap={8} align="center">
            <HrefButton
              href={`https://app.fieldnation.com/support-cases?search=${supportCase.caseId}&openCase=1`}
            >
              {supportCase.caseId}
            </HrefButton>
            <ActionIcon
              variant="transparent"
              size="xs"
              onClick={() => {
                clipboard.copy(supportCase.caseId);
                notifications.show({
                  message: 'Case ID copied to clipboard',
                  color: 'green',
                });
              }}
            >
              <CopyIcon />
            </ActionIcon>
          </Group>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Status
          </Text>
          <SupportCaseStatusBadge status={supportCase.status} />
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Work Order #
          </Text>
          <Group gap={8} align="top">
            {supportCase.workOrderId && supportCase.workOrderExternalId ? (
              <AnchorButton
                onClick={() =>
                  sidebarStack.pushItem('workOrderDetails', {
                    workOrderId: supportCase.workOrderId!,
                    workOrderExternalId: supportCase.workOrderExternalId!,
                  })
                }
              >
                {supportCase.workOrderExternalId}
              </AnchorButton>
            ) : (
              <Text size="sm" fw={600}>
                -
              </Text>
            )}
          </Group>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Opened By
          </Text>
          <Text size="sm" fw={600}>
            {`${supportCase.createdBy?.firstName} ${supportCase.createdBy?.lastName}`}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Opened
          </Text>
          <Text size="sm" fw={600}>
            {DateTime.fromISO(supportCase.openedAt).toRelative()}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Last Message
          </Text>
          <Text size="sm" fw={600}>
            {supportCase.lastMessageAt
              ? DateTime.fromISO(supportCase.lastMessageAt).toRelative()
              : '-'}
          </Text>
        </Stack>
      </SimpleGrid>
      <Stack gap="sm" mt="sm">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Problem
          </Text>
          <Text size="sm" fw={600}>
            {supportCase.problemType}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Subcategory
          </Text>
          <Text size="sm" fw={600}>
            {supportCase.subCategory.trim() === ''
              ? '-'
              : supportCase.subCategory}
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
}
